import React, {
    useState,
    useEffect,
    useContext,
    useRef,
    useMemo
}
from "react";

import {
    useParams,
    useHistory
}
from "react-router-dom";

import {
    store
}
from "../../../context/store";

import {
    functions
}
from "../../../constants/common-functions";

import {
    COLOR_UTILISATION_OCCUPIED,
    OMNISCAN_CORPORATE_TURQUOIS
}
from "../../../constants/colors";

import {
    TRUE
}
from "../../../constants/general-constants";

import {
    EMPTY_FILTER_BEAN
}
from "../../../constants/filter-constants";

import {
    nonStandardUtilisationUrls
}
from "../../../constants/client-specifics";

import AmChartRingIndicator
from "../../../components/AmChartRingIndicator";

import AmChartSimpleBar
from "../../../components/AmChartSimpleBar";

import AmChartStacked
from "../../../components/AmChartStacked";

import UtilisationKpiBox
from "../components/UtilisationKpiBox";

import UtilisationKpiBoxDate
from "../components/UtilisationKpiBoxDate";

import UtilisationChartProfileUnused
from "../components/UtilisationChartProfileUnused";

import UtilisationChartEfficiency
from "../components/UtilisationChartEfficiency";

import MobileHeader
from "../../home/mobile/components/MobileHeader";

import MobileDashboardPhotoNavigator
from "./components/MobileDashboardPhotoNavigator";

import "./MobileUtilisationAll.css";


export default function MobileUtilisationAllMeeting()
{
    //--------------------------------------------------------
    // Context
    //--------------------------------------------------------

    const globalState =
        useContext(store);

    const queryContext =
        globalState?.state?.queryContext;

    const history =
        useHistory();


    //--------------------------------------------------------
    // Parameters
    //--------------------------------------------------------

    const facilityIdParam =
        useParams()["facilityId"];

    const spaceTypeIdParam =
        useParams()["spaceTypeId"];


    //--------------------------------------------------------
    // KPI State
    //--------------------------------------------------------

    const [utilisationAvg, setUtilisationAvg] =
        useState(0);

    const [utilisationMax, setUtilisationMax] =
        useState(0);

    const [seats, setSeats] =
        useState(0);

    const [workplaces, setWorkplaces] =
        useState(0);

    const [observations, setObservations] =
        useState(0);

    const [datePeak, setDatePeak] =
        useState("");

    const [dateMin, setDateMin] =
        useState("");

    const [timePeak, setTimePeak] =
        useState("");

    const [timeMin, setTimeMin] =
        useState("");


    //--------------------------------------------------------
    // Chart State
    //--------------------------------------------------------

    const [leftBarChartData, setLeftBarChartData] =
        useState([]);

    const [leftBarChartHeight, setLeftBarChartHeight] =
        useState("220px");

    const [dataTimeline, setDataTimeline] =
        useState([]);

    const [activityDefs, setActivityDefs] =
        useState([]);


    //--------------------------------------------------------
    // Navigation / Filter State
    //--------------------------------------------------------

    const [filterBean, setFilterBean] =
        useState(EMPTY_FILTER_BEAN);

    const [facilityId, setFacilityId] =
        useState("-100");

    const [floorId, setFloorId] =
        useState("-100");

    const [floorZoneId, setFloorZoneId] =
        useState("-100");

    const [spaceId, setSpaceId] =
        useState("-100");

    const [spaceTypeId, setSpaceTypeId] =
        useState("-100");

    const [parentSpaceTypeId, setParentSpaceTypeId] =
        useState(
            spaceTypeIdParam || "-100"
        );

    const [organisationId, setOrganisationId] =
        useState("-100");

    const [parentOrganisationId, setParentOrganisationId] =
        useState("-100");

    const [showCoreDayOnly, setShowCoreDayOnly] =
        useState(0);

    const [showDesksMeetings, setShowDesksMeetings] =
        useState("-100");


    //--------------------------------------------------------
    // Other State
    //--------------------------------------------------------

    const [loaded, setLoaded] =
        useState(false);

    const [showMenu, setShowMenu] =
        useState(false);

    const [showFilters, setShowFilters] =
        useState(false);

    const refFacility =
        useRef(null);


    //--------------------------------------------------------
    // Initialisation
    //--------------------------------------------------------

    useEffect(
        () =>
        {
            if(loaded)
            {
                return;
            }

            resetSearch();

            setLoaded(true);
        },
        []
    );


    //--------------------------------------------------------
    // URL Space Type
    //--------------------------------------------------------

    useEffect(
        () =>
        {
            const value =
                spaceTypeIdParam ||
                "-100";

            setParentSpaceTypeId(
                value
            );

        },
        [
            spaceTypeIdParam
        ]
    );


    //--------------------------------------------------------
    // Activity Types
    //--------------------------------------------------------

    useEffect(
        () =>
        {
            functions.getArrayFromAjax(
                "/resolutionsoftwareplatform/utilisation/activity-types-used",
                handleActivityTypes,
                null
            );
        },
        [
            parentSpaceTypeId
        ]
    );


    const handleActivityTypes =
        (results) =>
        {
            const mapped =
                results.map(
                    (activity) =>
                    ({
                        id:
                            String(
                                activity.id
                            ),

                        title:
                            activity.name,

                        keyColumn:
                            String(
                                activity.id
                            ),

                        color:
                            activity.color ||
                            "#999"
                    })
                );

            setActivityDefs(
                mapped
            );
        };


    //--------------------------------------------------------
    // Activity Graph Definitions
    //--------------------------------------------------------

    const graphsTimeline =
        useMemo(
            () =>
            {
                if(
                    !activityDefs.length ||
                    !dataTimeline.length
                )
                {
                    return [];
                }

                return Object.keys(
                    dataTimeline[0]
                )
                .filter(
                    (key) =>
                        key !== "title" &&
                        key !== "0"
                )
                .map(
                    (key) =>
                    {
                        const definition =
                            activityDefs.find(
                                (activity) =>
                                    activity.id ===
                                    String(key)
                            );

                        return {
                            id:
                                String(key),

                            title:
                                definition
                                    ?
                                    definition.title
                                    :
                                    "Activity " + key,

                            keyColumn:
                                String(key),

                            color:
                                definition
                                    ?
                                    definition.color
                                    :
                                    "#999"
                        };
                    }
                );
            },
            [
                activityDefs,
                dataTimeline
            ]
        );


    //--------------------------------------------------------
    // Load Dashboard
    //--------------------------------------------------------

    useEffect(
        () =>
        {
            if(!loaded)
            {
                return;
            }

            if(
                nonStandardUtilisationUrls.includes(
                    window.location.hostname
                )
            )
            {
                history.push(
                    "/utilisationallnonstandard/-100"
                );

                return;
            }

            if(
                parentSpaceTypeId === "-100" ||
                parentSpaceTypeId === -100
            )
            {
                return;
            }


            //------------------------------------------------
            // Meeting Room Utilisation
            //------------------------------------------------

            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationaveragemeetingroom",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                populateUtilisationAvg
            );

            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationpeakmeetingroom",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                populateUtilisationMax
            );


            //------------------------------------------------
            // KPIs
            //------------------------------------------------

            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetcapacity",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                populateSeats
            );

            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetworkplacecount",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                populateWorkplaces
            );

            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetobservations",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                populateObservations
            );

            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetdatemax",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                calculateDatePeak
            );

            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetdatemin",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                calculateDateMin
            );


            //------------------------------------------------
            // Breakdown
            //------------------------------------------------

            if(
                String(facilityId) === "-100"
            )
            {
                functions.postFilterBeanAjax(
                    "/resolutionsoftwareplatform/utilisationstandardgetavgbyfacility",
                    functions.buildPostBodyFilterBeanQueryContext(
                        filterBean,
                        queryContext
                    ),
                    populateDataByFacility
                );
            }
            else if(
                String(floorId) === "-100"
            )
            {
                functions.postFilterBeanAjax(
                    "/resolutionsoftwareplatform/utilisationstandardgetavgbyfloor",
                    functions.buildPostBodyFilterBeanQueryContext(
                        filterBean,
                        queryContext
                    ),
                    populateDataByFloor
                );
            }
            else if(
                String(spaceId) === "-100"
            )
            {
                functions.postFilterBeanAjax(
                    "/resolutionsoftwareplatform/utilisationstandardgetavgbyspace",
                    functions.buildPostBodyFilterBeanQueryContext(
                        filterBean,
                        queryContext
                    ),
                    populateDataBySpace
                );
            }


            //------------------------------------------------
            // Activity Timeline
            //------------------------------------------------

            loadDataTimeline();

        },
        [
            loaded,
            parentSpaceTypeId,
            filterBean,
            queryContext?.utilisationSurveyId
        ]
    );


    //--------------------------------------------------------
    // Chart Height
    //--------------------------------------------------------

    useEffect(
        () =>
        {
            const calculatedHeight =
                (
                    28 *
                    leftBarChartData.length
                )
                +
                80;

            setLeftBarChartHeight(
                Math.max(
                    calculatedHeight,
                    220
                )
                +
                "px"
            );
        },
        [
            leftBarChartData
        ]
    );


    //--------------------------------------------------------
    // Reset
    //--------------------------------------------------------

    const resetSearch =
        () =>
        {
            setFacilityId(
                facilityIdParam ||
                "-100"
            );

            setFloorId("-100");

            setFloorZoneId("-100");

            setSpaceId("-100");

            setSpaceTypeId("-100");

            setOrganisationId("-100");

            setParentOrganisationId("-100");

            setShowCoreDayOnly(0);
        };


    //--------------------------------------------------------
    // Timeline
    //--------------------------------------------------------

    const loadDataTimeline =
        () =>
        {
            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetactivityovertime",
                functions.buildPostBodyFilterBeanQueryContext(
                    filterBean,
                    queryContext
                ),
                handleDataTimeline
            );
        };


    const handleDataTimeline =
        (obj) =>
        {
            if(
                !obj ||
                !Array.isArray(
                    obj.returnObjects
                )
            )
            {
                setDataTimeline([]);

                return;
            }

            const cleanedData =
                obj.returnObjects.map(
                    (row) =>
                    {
                        const newRow =
                        {
                            title:
                                row.title
                        };

                        Object.keys(row).forEach(
                            (key) =>
                            {
                                if(
                                    key === "title" ||
                                    key === "0"
                                )
                                {
                                    return;
                                }

                                newRow[key] =
                                    row[key];
                            }
                        );

                        return newRow;
                    }
                );

            setDataTimeline(
                cleanedData
            );
        };


    //--------------------------------------------------------
    // KPI Handlers
    //--------------------------------------------------------

    const populateSeats =
        (obj) =>
        {
            setSeats(
                obj.returnDouble
            );
        };


    const populateWorkplaces =
        (obj) =>
        {
            setWorkplaces(
                obj.returnDouble
            );
        };


    const populateObservations =
        (obj) =>
        {
            setObservations(
                obj.returnDouble
            );
        };


    const populateUtilisationAvg =
        (obj) =>
        {
            setUtilisationAvg(
                obj.returnDouble
            );
        };


    const populateUtilisationMax =
        (obj) =>
        {
            const value =
                obj.returnDouble;

            setUtilisationMax(
                parseInt(value) > 100
                    ?
                    100
                    :
                    value
            );
        };


    const calculateDatePeak =
        (input) =>
        {
            setTimePeak(
                input.hour
            );

            setDatePeak(
                input.returnDate
            );
        };


    const calculateDateMin =
        (input) =>
        {
            setTimeMin(
                input.hour
            );

            setDateMin(
                input.returnDate
            );
        };


    //--------------------------------------------------------
    // Breakdown Data
    //--------------------------------------------------------

    const populateDataByFacility =
        (results) =>
        {
            const data =
                results.map(
                    (item) =>
                    ({
                        id:
                            item.id,

                        label:
                            item.name,

                        value:
                            item.avg
                    })
                );

            setLeftBarChartData(
                data
            );
        };


    const populateDataByFloor =
        (results) =>
        {
            const data =
                results.map(
                    (item) =>
                    ({
                        id:
                            item.id,

                        label:
                            item.name,

                        value:
                            item.avg
                    })
                );

            setLeftBarChartData(
                data
            );
        };


    const populateDataBySpace =
        (results) =>
        {
            const data =
                results.map(
                    (item) =>
                    ({
                        id:
                            item.id,

                        label:
                            item.name ||
                            item.code,

                        value:
                            item.avg
                    })
                );

            setLeftBarChartData(
                data
            );
        };


    //--------------------------------------------------------
    // Breakdown Navigation
    //--------------------------------------------------------

    const handleBarClick =
        (id) =>
        {
            if(
                String(facilityId) === "-100"
            )
            {
                setFacilityId(
                    String(id)
                );
            }
            else if(
                String(floorId) === "-100"
            )
            {
                setFloorId(
                    String(id)
                );
            }
            else
            {
                setSpaceId(
                    String(id)
                );
            }
        };


    //--------------------------------------------------------
    // Render
    //--------------------------------------------------------

    return (

        <div
            style={{
                display:
                    "flex",

                flexDirection:
                    "column",

                width:
                    "100%",

                height:
                    "100vh",

                overflow:
                    "hidden"
            }}
        >

            <MobileHeader

                openMenu={
                    () =>
                        setShowMenu(true)
                }

                openSearch={
                    () =>
                        console.log(
                            "SEARCH"
                        )
                }

            />


            {/*----------------------------------------------
                Mobile Filters
            ----------------------------------------------*/}

            {
                showFilters &&
                (
                    <MobileDashboardPhotoNavigator

                        display={
                            showFilters
                        }

                        closeFunction={
                            () =>
                                setShowFilters(false)
                        }

                        refFacility={
                            refFacility
                        }

                        facilityId={
                            facilityId
                        }

                        setFacilityId={
                            setFacilityId
                        }

                        floorId={
                            floorId
                        }

                        setFloorId={
                            setFloorId
                        }

                        floorZoneId={
                            floorZoneId
                        }

                        setFloorZoneId={
                            setFloorZoneId
                        }

                        spaceId={
                            spaceId
                        }

                        setSpaceId={
                            setSpaceId
                        }

                        parentSpaceTypeId={
                            parentSpaceTypeId
                        }

                        setParentSpaceTypeId={
                            setParentSpaceTypeId
                        }

                        spaceTypeId={
                            spaceTypeId
                        }

                        setSpaceTypeId={
                            setSpaceTypeId
                        }

                        parentOrganisationId={
                            parentOrganisationId
                        }

                        setParentOrganisationId={
                            setParentOrganisationId
                        }

                        organisationId={
                            organisationId
                        }

                        setOrganisationId={
                            setOrganisationId
                        }

                        showCoreDayOnly={
                            showCoreDayOnly
                        }

                        setShowCoreDayOnly={
                            setShowCoreDayOnly
                        }

                        showDesksMeetings={
                            showDesksMeetings
                        }

                        setShowDesksMeetings={
                            setShowDesksMeetings
                        }

                        resetFunction={
                            resetSearch
                        }

                        setFilterBean={
                            setFilterBean
                        }

                    />
                )
            }


            <div className="mobile-utilisation-all-page">

                <div className="mobile-utilisation-all">


                    {/*--------------------------------------
                        Heading
                    --------------------------------------*/}

                    <div className="mobile-utilisation-all-heading">

                        <div className="mobile-utilisation-all-title">

                            Meeting Room Utilisation

                        </div>

                        <div className="mobile-utilisation-all-subtitle">

                            Meeting room utilisation,
                            occupancy and activity patterns.

                        </div>

                    </div>


                    {/*--------------------------------------
                        Filters
                    --------------------------------------*/}

                    <button

                        className="mobile-utilisation-all-filter"

                        type="button"

                        onClick={
                            () =>
                                setShowFilters(true)
                        }

                    >

                        <span>

                            Filters &amp; Location

                        </span>

                        <span>

                            ›

                        </span>

                    </button>


                    {/*--------------------------------------
                        Indicators
                    --------------------------------------*/}

                    <div className="mobile-utilisation-all-indicators">

                        <div className="mobile-utilisation-all-ring">

                            <AmChartRingIndicator

                                percentage={
                                    utilisationAvg
                                }

                                width={
                                    130
                                }

                                height={
                                    130
                                }

                                icon="chair"

                                color={
                                    COLOR_UTILISATION_OCCUPIED
                                }

                                label="Avg"

                                id="mobilemeetingutilisationavg"

                            />

                        </div>


                        <div className="mobile-utilisation-all-ring">

                            <AmChartRingIndicator

                                percentage={
                                    utilisationMax
                                }

                                width={
                                    130
                                }

                                height={
                                    130
                                }

                                icon="chair"

                                color={
                                    COLOR_UTILISATION_OCCUPIED
                                }

                                label="Peak"

                                id="mobilemeetingutilisationpeak"

                            />

                        </div>

                    </div>


                    {/*--------------------------------------
                        KPIs
                    --------------------------------------*/}

                    <div className="mobile-utilisation-all-kpis">

                        <UtilisationKpiBox

                            title="Seats"

                            figure={
                                functions.formatNumber(
                                    seats,
                                    0
                                )
                            }

                            color={
                                OMNISCAN_CORPORATE_TURQUOIS
                            }

                            width="100%"

                        />


                        <UtilisationKpiBox

                            title="Worksettings"

                            figure={
                                functions.formatNumber(
                                    workplaces,
                                    0
                                )
                            }

                            color={
                                OMNISCAN_CORPORATE_TURQUOIS
                            }

                            width="100%"

                        />


                        <UtilisationKpiBox

                            title="Observations"

                            figure={
                                functions.formatNumber(
                                    observations,
                                    0
                                )
                            }

                            color={
                                OMNISCAN_CORPORATE_TURQUOIS
                            }

                            width="100%"

                        />


                        <UtilisationKpiBoxDate

                            title="Max On"

                            figure={
                                functions.formatDate(
                                    datePeak
                                )
                            }

                            hour={
                                timePeak
                            }

                            width="100%"

                        />


                        <UtilisationKpiBoxDate

                            title="Min On"

                            figure={
                                functions.formatDate(
                                    dateMin
                                )
                            }

                            hour={
                                timeMin
                            }

                            width="100%"

                        />

                    </div>


                    {/*--------------------------------------
                        Breakdown
                    --------------------------------------*/}

                    <div className="mobile-utilisation-all-card">

                        <div className="mobile-utilisation-all-card-title">

                            Breakdown

                        </div>

                        <div className="mobile-utilisation-all-card-subtitle">

                            Building / Floor / Workplace

                        </div>

                        <div className="mobile-utilisation-all-chart">

                            <AmChartSimpleBar

                                chartId="mobilemeetingutilisationbar"

                                chartData={
                                    leftBarChartData
                                }

                                categoryKey="label"

                                valueKey="value"

                                width="100%"

                                height={
                                    leftBarChartHeight
                                }

                                onClick={
                                    handleBarClick
                                }

                                color={
                                    COLOR_UTILISATION_OCCUPIED
                                }

                            />

                        </div>

                    </div>


                    {/*--------------------------------------
                        Profile
                    --------------------------------------*/}

                    <div className="mobile-utilisation-all-card">

                        <UtilisationChartProfileUnused

                            id="mobilemeetingprofileunused"

                            filterBean={
                                filterBean
                            }

                            isMeeting={
                                TRUE
                            }

                            parentSpaceTypeId={
                                parentSpaceTypeId
                            }

                        />

                    </div>


                    {/*--------------------------------------
                        Efficiency
                    --------------------------------------*/}

                    <div className="mobile-utilisation-all-card">

                        <UtilisationChartEfficiency

                            id="mobilemeetingefficiency"

                            filterBean={
                                filterBean
                            }

                            isMeeting={
                                TRUE
                            }

                        />

                    </div>


                    {/*--------------------------------------
                        Activity Profile
                    --------------------------------------*/}

                    <div className="mobile-utilisation-all-card">

                        <div className="mobile-utilisation-all-card-title">

                            Utilisation Profile

                        </div>

                        <div className="mobile-utilisation-all-card-subtitle">

                            Meeting room activity over time

                        </div>

                        <div className="mobile-utilisation-all-timeline">

                            {
                                dataTimeline &&
                                dataTimeline.length > 0 &&
                                graphsTimeline &&
                                graphsTimeline.length > 0 &&
                                (
                                    <AmChartStacked

                                        chartId="mobilemeetingactivity"

                                        width="100%"

                                        height="300px"

                                        chartData={
                                            dataTimeline
                                        }

                                        chartKeys={
                                            graphsTimeline
                                        }

                                        categoryKey="title"

                                        legendPosition="bottom"

                                    />
                                )
                            }

                        </div>

                    </div>


                </div>

            </div>

        </div>

    );
}